import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './ListOption.module.css';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { useSlice } from '../../hooks/useSlice';
import { selectTotalPublicNum } from '../../services/search';
import { useGetSortByQuery } from '../../services/common';
import { selectAllResource, useInitDriveQuery } from '../../services/drive';

import { selectMenu } from '../../features/infoSlice';
import {
  selectSearchInpVal,
  selectSearchWord,
} from '../../features/searchSlice';
import { driveSharedToggled } from '../../features/driveSlice';

import Icon from '../../components/Icon';
import Dropdown from '../Dropdown';
import Button from '../Button';
import EditBox from './EditBox';
import {
  OPTION_ICON,
  SEARCH_RESULT,
  OPTION,
  EBOOK_LIST_EDIT,
  EDIT_TRASH,
  EMPTY_TRASH,
  ITEMS_SELECTED_MESSAGE,
  ASCENDING,
  DESCENDING,
  SORT,
} from '../../constants';
import { Options } from '../../types/library';
import { isMobile } from '../../utils/util';
import useModal from '../../hooks/useModal';
import { Resource } from '../../types/drive';
import { EntityId } from '@reduxjs/toolkit';
import { TooltipWrapper } from '../Tooltip';
import { SortDirection, SortType } from '../../types/common';

export default function ListOption() {
  const dispatch = useAppDispatch();
  const cx = classNames.bind(styles);
  const slice = useSlice();
  const { toggleModal } = useModal();
  const menu = useAppSelector(selectMenu);
  const tag =
    menu === 'library' ? 'personal' : menu === 'mydrive' ? 'archive' : menu;
  const { data, isLoading } =
    tag === 'archive' ? useInitDriveQuery() : useGetSortByQuery(tag);
  const sortByMap = data?.sortByMap || data;

  const buttonRef = useRef(null);
  const optionsButtonRef = useRef(null);
  const searchWord = useAppSelector(selectSearchWord);
  const searchInpVal = useAppSelector(selectSearchInpVal);
  const searchedResultsLen = useAppSelector(selectTotalPublicNum);

  const {
    selectOptions,
    sortTypeChanged,
    orderChanged,
    layoutTypeChanged,
    editModeToggled,
    itemSelected,
    detailsToggled,
    selectSelectedIds,
  } = slice;
  const {
    sortType,
    sortOrder,
    layoutType,
    isEditMode,
    isDetailsOpened,
    isShowShared,
  }: Options = useAppSelector(selectOptions);
  const selectedIds = useAppSelector(selectSelectedIds);
  const driveProjects: Resource[] = useAppSelector(selectAllResource);

  const sharedDriveIds = driveProjects.reduce((acc: EntityId[], cur) => {
    cur.share && acc.push(cur.resource.resourceId);
    return acc;
  }, []);
  const notSharedSelectedIds = selectedIds.filter(
    (id) => !sharedDriveIds.includes(id)
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeButtons, setActiveButtons] = useState<string[]>([]);
  const [isOpenOptions, setIsOpenOptions] = useState(false);

  const isTrash = menu === 'trash';

  const toggleDropdownMenu = () => {
    setIsDropdownOpen(!isDropdownOpen);
    dispatch(detailsToggled(false));
  };

  const selectDropdownList = (name: string) => {
    if (name === 'edit') {
      toggleEditMode();
    } else if (name === 'empty') {
      toggleModal({
        name: 'emptyTrash',
        id: 'confirmEmptyTrash',
        type: 'confirm',
        initialButtonDisabled: false,
      });
    } else {
      sortList(name);
    }
    toggleOptions();
  };

  const changeSortOrder = (direction: SortDirection) => {
    dispatch(orderChanged(direction.toLocaleUpperCase() as SortDirection));
    setIsDropdownOpen(false);
    dispatch(detailsToggled(false));
  };

  const changeSortType = (type: SortType) => {
    dispatch(sortTypeChanged(type));
    setIsDropdownOpen(false);
    dispatch(detailsToggled(false));
  };

  const isSortDirection = (value: string): value is SortDirection => {
    return Object.values(SortDirection).includes(value as SortDirection);
  };

  const isSortType = (value: string): value is SortType => {
    return Object.values(SortType).includes(value as SortType);
  };

  const sortList = (name: string) => {
    if (isSortDirection(name)) {
      changeSortOrder(name);
    } else if (isSortType(name)) {
      changeSortType(name);
    } else {
      console.warn(`Unknown sort parameter: ${name}`);
    }
  };

  const changeLayout = () => {
    const type = layoutType === 'grid' ? 'list' : 'grid';
    dispatch(layoutTypeChanged(type));
    dispatch(detailsToggled(false));
  };

  const toggleEditMode = () => {
    dispatch(editModeToggled(!isEditMode));
    dispatch(itemSelected({ ids: [], item: null }));
    dispatch(detailsToggled(false));
    menu === 'mydrive' && dispatch(driveSharedToggled(true));
  };

  const toggleDetails = () => {
    dispatch(detailsToggled());
    !isEditMode && dispatch(itemSelected({ ids: [], item: null }));
  };

  const optionList = [
    {
      name: 'layout',
      btnClass: 'layout',
      clickHandler: changeLayout,
      icon: layoutType,
    },
    {
      name: 'edit',
      btnClass: 'edit',
      clickHandler: toggleEditMode,
      icon: 'edit',
    },
    {
      name: 'info',
      btnClass: 'info',
      clickHandler: toggleDetails,
      icon: 'info',
    },
  ];

  const toggleOptions = () => {
    setIsOpenOptions(!isOpenOptions);
  };

  useEffect(() => {
    const activeButtons =
      isEditMode && isDetailsOpened
        ? ['edit', 'info']
        : isEditMode
        ? ['edit']
        : isDetailsOpened
        ? ['info']
        : [];

    setActiveButtons(activeButtons);
  }, [isEditMode, isDetailsOpened]);

  if (isLoading) return null;

  return (
    <>
      <div className={cx('library_option', { edit_mode: isEditMode })}>
        {!isMobile && layoutType === 'grid' && !isEditMode && (
          <div className={cx('option_order')}>
            <span className={cx('is_order')}>
              <TooltipWrapper
                content={SORT}
                onClick={toggleDropdownMenu}
                externalRef={buttonRef}
              >
                {(tooltipProps) => (
                  <button
                    className={cx(
                      'btn',
                      'order_arrow',
                      sortOrder.toLowerCase(),
                      {
                        on: isDropdownOpen,
                      }
                    )}
                    {...tooltipProps}
                  >
                    <span className={cx('blind')}>{SORT}</span>
                  </button>
                )}
              </TooltipWrapper>
            </span>
            <Dropdown
              name="sortType"
              classnames={['type_order_menu']}
              type={sortType}
              list={Object.keys({
                ...sortByMap,
                asc: ASCENDING,
                desc: DESCENDING,
              })}
              clickHandlers={[sortList]}
              isOpen={isDropdownOpen}
              button={buttonRef}
              backdropHandler={() => setIsDropdownOpen(false)}
              data={{
                ...sortByMap,
                asc: ASCENDING,
                desc: DESCENDING,
              }}
              sortOrder={sortOrder.toLowerCase() as 'asc' | 'desc'}
            />
          </div>
        )}

        {isEditMode && <EditBox />}

        {!isMobile &&
          optionList.map(
            ({ name, btnClass, clickHandler, icon }) =>
              (!isEditMode || name !== 'layout') &&
              (menu !== 'search' || name !== 'edit') &&
              (!isMobile || (isMobile && name !== 'layout')) && (
                <span key={name} className={cx(`option_${name}`)}>
                  <TooltipWrapper
                    content={OPTION_ICON[icon]}
                    onClick={clickHandler}
                  >
                    {(tooltipProps) => (
                      <Button
                        name={OPTION_ICON[icon]}
                        classnames={[
                          `book_${btnClass}`,
                          {
                            on: activeButtons.includes(name),
                          },
                        ]}
                        isBlindName={true}
                        tooltipProps={tooltipProps}
                      >
                        <Icon name={icon} />
                      </Button>
                    )}
                  </TooltipWrapper>
                </span>
              )
          )}

        {isMobile && (
          <>
            {!isEditMode && (
              <Button
                name={OPTION}
                classnames={['open_mobile_option']}
                isBlindName={true}
                handleClick={toggleOptions}
                ref={optionsButtonRef}
              >
                <i className={cx('svg_i')}></i>
              </Button>
            )}
            <Dropdown
              name="options"
              classnames={['type_order_menu', 'options']}
              type={sortType}
              list={[
                ...Object.keys(sortByMap),
                'edit',
                ...(isTrash ? ['empty'] : []),
              ]}
              clickHandlers={[selectDropdownList]}
              isOpen={isOpenOptions}
              button={optionsButtonRef}
              backdropHandler={() => setIsOpenOptions(false)}
              data={{
                ...sortByMap,
                edit: isTrash ? EDIT_TRASH : EBOOK_LIST_EDIT,
                ...(isTrash && { empty: EMPTY_TRASH }),
              }}
            />
          </>
        )}
      </div>
      {(menu === 'search'
        ? searchWord && searchWord === searchInpVal
        : isEditMode) && (
        <div className={cx('library_select_text')}>
          <p
            className={cx('desc')}
            dangerouslySetInnerHTML={{
              __html: (isEditMode
                ? ITEMS_SELECTED_MESSAGE
                : SEARCH_RESULT
              ).replace(
                '{N}',
                (isEditMode
                  ? isShowShared
                    ? selectedIds.length
                    : notSharedSelectedIds.length
                  : searchedResultsLen
                ).toString()
              ),
            }}
          ></p>
        </div>
      )}
    </>
  );
}
